import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'

import { CheckBoxOutlineBlank, CheckBox } from '@mui/icons-material'
import { Box, Checkbox, createFilterOptions } from '@mui/material'
import { StateSchema } from '@/hooks/form/models'

import { ApplicationState } from '@/state/root.models'
import { GamesList } from '@/state/wagering-contribution/models'
import AutocompleteCustom from '@/views/components/autocomplete-custom/index.component'

import { sentenceCase } from '@/utils'
import { BonusType } from '@/enums'
import { MAX_FREE_SPIN_GAMES } from '@/constants'

type Props = {
  handleOnChange: (name: string, value: any) => void
  state: StateSchema
  bonusType: string
}

const GamesComponent = ({ handleOnChange, state, bonusType }: Props) => {
  const { t } = useTranslation()

  const { games } = useSelector(
    ({ bonusEngine }: ApplicationState) => bonusEngine,
  )

  // free spins travel to BGaming as game codes, so a code is what identifies a
  // selected option, and BGaming refuses an issue with more than 99 of them
  const isFreeSpins = bonusType === BonusType.FREE_SPINS

  const getOptionId = (option: any) =>
    isFreeSpins ? option.gameCode : option.gameProviderId
  //HERE
  // const filterGames =
  //   games.length > 0
  //     ? games.map((game: GamesList) => {
  //         return {
  //           type: bonusType,
  //           validationId: game.id.toString(),
  //           vendorExternalID: game.vendorExternalID,
  //           label: game.name,
  //           action: 1,
  //         }
  //       })
  //     : []

  const selectedProviders = state.providers?.value ?? []

  const filterGames =
    games.length > 0
      ? games
          .filter((game: GamesList) => {
            if (selectedProviders.length === 0) return false

            const isProviderSelected = selectedProviders.some(
              (provider: any) =>
                Number(provider.value) === Number(game.vendorExternalID),
            )

            if (!isProviderSelected) return false

            if (isFreeSpins) {
              return Boolean(game.allowFreeSpins)
            }

            return true
          })
          .map((game: GamesList) => ({
            type: bonusType,
            validationId: game.id.toString(),
            vendorExternalID: game.vendorExternalID,
            gameCode: game.gameCode,
            label: game.name,
            action: 1,
          }))
      : []

  // BGaming takes at most 99 games per issue, so selecting all of them for a
  // free spins bonus can only reach as far as that cap
  const selectableGames = isFreeSpins
    ? filterGames.slice(0, MAX_FREE_SPIN_GAMES)
    : filterGames

  const allSelected = selectableGames.length === state.games.value.length

  const reachedGamesLimit =
    isFreeSpins && state.games.value.length >= MAX_FREE_SPIN_GAMES

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      handleOnChange('games', selectableGames)
    } else {
      handleOnChange('games', [])
    }
  }

  const handleToggleSelectAll = () => {
    handleSelectAll && handleSelectAll(!allSelected)
  }

  const handleChangeGames = (
    event: any,
    selectedOptions: any[],
    reason: string,
  ) => {
    if (reason === 'selectOption' || reason === 'removeOption') {
      if (selectedOptions.find((option) => option.value === 'select-all')) {
        handleToggleSelectAll()
      } else if (reason === 'removeOption') {
        handleOnChange('games', selectedOptions)
      } else {
        const lastOption = selectedOptions[selectedOptions.length - 1]

        const findValue =
          selectedOptions.length > 0 &&
          state.games.value.find(
            (item: any) => getOptionId(item) === getOptionId(lastOption),
          )

        if (findValue) {
          const removeValue = state.games.value.filter(
            (provider: any) =>
              getOptionId(provider) !== getOptionId(lastOption),
          )
          handleOnChange('games', removeValue)
        } else if (!reachedGamesLimit) {
          handleOnChange('games', selectedOptions)
        }
      }
    } else if (reason === 'clear') {
      handleOnChange('games', [])
    }
  }

  const filter = createFilterOptions()

  const providerValidation =
    state.providers?.value.length > 0 || state.providers.error.length > 0

  const gameValidation =
    (bonusType === BonusType.BETSOFT_CASINO || isFreeSpins) &&
    state.games?.value.length === 0 &&
    providerValidation

  const helperText = gameValidation
    ? sentenceCase(
        `${t('error.requiredFieldArray', {
          min: 1,
          field: 'game',
        })}`,
        true,
      )
    : isFreeSpins
      ? t('freeSpinGamesSelected', {
          count: state.games.value.length,
          max: MAX_FREE_SPIN_GAMES,
        })
      : ''

  return (
    <Box maxWidth={400} mb={2}>
      <AutocompleteCustom
        limitTags={2}
        multiple
        onChange={handleChangeGames}
        chipTags
        disabled={filterGames.length === 0}
        options={filterGames}
        value={state.games.value || []}
        label={t('labels.games')}
        freeSolo
        getOptionLabel={(option) =>
          `${option.label} ${getOptionId(option) ?? ''}`
        }
        error={gameValidation}
        helperText={helperText}
        getOptionDisabled={(option: any) =>
          reachedGamesLimit &&
          option.value !== 'select-all' &&
          !state.games.value.some(
            (game: any) => getOptionId(game) === getOptionId(option),
          )
        }
        filterOptions={(options: any, params: any) => {
          const filtered = filter(options, params)

          return [
            {
              label:
                isFreeSpins && filterGames.length > MAX_FREE_SPIN_GAMES
                  ? t('selectAllUpTo', { max: MAX_FREE_SPIN_GAMES })
                  : t('labels.selectAll'),
              value: 'select-all',
            },
            ...filtered,
          ]
        }}
        renderOption={(props, option) => {
          const selectAllProps =
            option.value === 'select-all' // To control the state of 'select-all' checkbox
              ? { checked: allSelected }
              : {}
          return (
            <li {...props} key={uuidv4()}>
              <Checkbox
                color="primary"
                icon={<CheckBoxOutlineBlank fontSize="small" />}
                checkedIcon={<CheckBox fontSize="small" />}
                style={{ marginRight: 8 }}
                checked={
                  state.games.value
                    ? state.games.value.filter((game: any) =>
                        isFreeSpins
                          ? getOptionId(game) === getOptionId(option)
                          : game.vendorExternalID === option.vendorExternalID,
                      ).length > 0
                    : false
                }
                {...selectAllProps}
              />
              {option.value === 'select-all' ? (
                <span className="select-all-items">
                  <em>{option.label}</em>
                </span>
              ) : isFreeSpins ? (
                `${option.label} - ${option.gameCode}`
              ) : (
                `${option.label} - ${option.vendorExternalID}`
              )}
            </li>
          )
        }}
      />
    </Box>
  )
}

export default GamesComponent
